/**
 * WorkOrderDetail
 *
 * Slide-up drawer showing full work order details including
 * status timeline, cost breakdown, photos, and activity log.
 * Supports inline status transitions and comment addition.
 */

import { useState, useEffect, useCallback } from "react";
import {
	Drawer,
	Tag,
	Button,
	Input,
	Spin,
	Divider,
	message,
	Timeline,
	Collapse,
} from "antd";
import {
	ClockCircleOutlined,
	CheckCircleOutlined,
	WarningOutlined,
	DollarOutlined,
	UserOutlined,
	PlayCircleOutlined,
	PauseCircleOutlined,
	CloseCircleOutlined,
	SendOutlined,
	ScanOutlined,
} from "@ant-design/icons";
import {
	getWorkOrder,
	updateWorkOrderStatus,
	addComment as apiAddComment,
	type WorkOrder,
	type WorkOrderStatus,
} from "@/services/work-order-service";
import {
	flGetScansByWorkOrder,
	type FacilityLensScan,
} from "@/services/facility-lens-service";
import "./WorkOrderDetail.scss";

const { TextArea } = Input;

interface WorkOrderDetailProps {
	open: boolean;
	workOrderId: number;
	onClose: () => void;
	onUpdated: () => void;
}

const STATUS_COLORS: Record<string, string> = {
	OPEN: "blue",
	ASSIGNED: "cyan",
	IN_PROGRESS: "processing",
	ON_HOLD: "warning",
	COMPLETED: "success",
	CLOSED: "default",
	CANCELLED: "error",
	DRAFT: "default",
};

const PRIORITY_LABELS: Record<number, { label: string; color: string }> = {
	1: { label: "Emergency", color: "#ff4d4f" },
	2: { label: "Urgent", color: "#fa8c16" },
	3: { label: "High", color: "#faad14" },
	4: { label: "Medium", color: "#1890ff" },
	5: { label: "Low", color: "#8c8c8c" },
};

// Valid next statuses per current status
const NEXT_ACTIONS: Record<string, { status: WorkOrderStatus; label: string; icon: React.ReactNode; color: string }[]> = {
	OPEN: [
		{ status: "ASSIGNED", label: "Assign", icon: <UserOutlined />, color: "#1890ff" },
		{ status: "CANCELLED", label: "Cancel", icon: <CloseCircleOutlined />, color: "#ff4d4f" },
	],
	ASSIGNED: [
		{ status: "IN_PROGRESS", label: "Start Work", icon: <PlayCircleOutlined />, color: "#52c41a" },
		{ status: "OPEN", label: "Unassign", icon: <UserOutlined />, color: "#8c8c8c" },
	],
	IN_PROGRESS: [
		{ status: "COMPLETED", label: "Complete", icon: <CheckCircleOutlined />, color: "#52c41a" },
		{ status: "ON_HOLD", label: "Put on Hold", icon: <PauseCircleOutlined />, color: "#faad14" },
	],
	ON_HOLD: [
		{ status: "IN_PROGRESS", label: "Resume", icon: <PlayCircleOutlined />, color: "#52c41a" },
		{ status: "CANCELLED", label: "Cancel", icon: <CloseCircleOutlined />, color: "#ff4d4f" },
	],
	COMPLETED: [
		{ status: "CLOSED", label: "Close", icon: <CheckCircleOutlined />, color: "#52c41a" },
		{ status: "IN_PROGRESS", label: "Reopen", icon: <PlayCircleOutlined />, color: "#fa8c16" },
	],
};

export const WorkOrderDetail = ({
	open,
	workOrderId,
	onClose,
	onUpdated,
}: WorkOrderDetailProps) => {
	const [wo, setWo] = useState<WorkOrder | null>(null);
	const [loading, setLoading] = useState(true);
	const [comment, setComment] = useState("");
	const [submittingComment, setSubmittingComment] = useState(false);
	const [transitioning, setTransitioning] = useState(false);
	const [flScans, setFlScans] = useState<FacilityLensScan[]>([]);

	const fetchDetail = useCallback(async () => {
		setLoading(true);
		try {
			const data = await getWorkOrder(workOrderId);
			setWo(data);
		} catch {
			message.error("Failed to load work order");
		} finally {
			setLoading(false);
		}
	}, [workOrderId]);

	useEffect(() => {
		if (open && workOrderId) {
			fetchDetail();
			flGetScansByWorkOrder(workOrderId).then(setFlScans).catch(() => {});
		}
	}, [open, workOrderId, fetchDetail]);

	const handleStatusChange = async (newStatus: WorkOrderStatus) => {
		if (!wo) return;
		setTransitioning(true);
		try {
			await updateWorkOrderStatus(wo.id, newStatus);
			message.success(`Status updated to ${newStatus.replace(/_/g, " ")}`);
			await fetchDetail();
			onUpdated();
		} catch (error: any) {
			message.error(error.message || "Failed to update status");
		} finally {
			setTransitioning(false);
		}
	};

	const handleAddComment = async () => {
		if (!comment.trim() || !wo) return;
		setSubmittingComment(true);
		try {
			await apiAddComment(wo.id, comment.trim());
			setComment("");
			await fetchDetail();
		} catch {
			message.error("Failed to add comment");
		} finally {
			setSubmittingComment(false);
		}
	};

	const formatDate = (d: string | null) => {
		if (!d) return "—";
		return new Date(d).toLocaleString(undefined, {
			month: "short",
			day: "numeric",
			hour: "numeric",
			minute: "2-digit",
		});
	};

	const priorityInfo = wo ? PRIORITY_LABELS[wo.priority] ?? { label: "Unknown", color: "#8c8c8c" } : null;
	const nextActions = wo ? (NEXT_ACTIONS[wo.status] ?? []) : [];

	return (
		<Drawer
			open={open}
			onClose={onClose}
			placement="bottom"
			height="85vh"
			className="wo-detail-drawer"
			title={null}
			destroyOnClose
		>
			{loading || !wo ? (
				<div className="wo-detail-loading">
					<Spin size="large" />
				</div>
			) : (
				<div className="wo-detail">
					{/* Header */}
					<div className="wo-detail-header">
						<div className="wo-detail-header-top">
							<span className="wo-detail-number">{wo.workOrderNumber}</span>
							<Tag color={STATUS_COLORS[wo.status]} className="wo-detail-status">
								{wo.status.replace(/_/g, " ")}
							</Tag>
						</div>
						<h2 className="wo-detail-title">{wo.title}</h2>
						<div className="wo-detail-meta">
							<Tag
								style={{ color: priorityInfo!.color, borderColor: priorityInfo!.color }}
							>
								P{wo.priority} {priorityInfo!.label}
							</Tag>
							<Tag>{wo.workType}</Tag>
							{wo.isSlaBreached && (
								<Tag color="error" icon={<WarningOutlined />}>SLA Breached</Tag>
							)}
						</div>
					</div>

					{/* Info Grid */}
					<div className="wo-detail-grid">
						<div className="wo-detail-info-item">
							<span className="wo-detail-info-label">Location</span>
							<span className="wo-detail-info-value">{wo.locationName ?? "—"}</span>
						</div>
						<div className="wo-detail-info-item">
							<span className="wo-detail-info-label">Asset</span>
							<span className="wo-detail-info-value">{wo.assetTag ?? "N/A"}</span>
						</div>
						<div className="wo-detail-info-item">
							<span className="wo-detail-info-label">Assigned To</span>
							<span className="wo-detail-info-value">
								{wo.assignedUserName ?? wo.assignedVendorName ?? "Unassigned"}
							</span>
						</div>
						<div className="wo-detail-info-item">
							<span className="wo-detail-info-label">Created By</span>
							<span className="wo-detail-info-value">{wo.createdByUserName ?? "—"}</span>
						</div>
						<div className="wo-detail-info-item">
							<span className="wo-detail-info-label">SLA Deadline</span>
							<span className="wo-detail-info-value">{formatDate(wo.slaDeadline)}</span>
						</div>
						<div className="wo-detail-info-item">
							<span className="wo-detail-info-label">Due Date</span>
							<span className="wo-detail-info-value">{formatDate(wo.dueDate)}</span>
						</div>
					</div>

					{wo.description && (
						<div className="wo-detail-description">
							<p>{wo.description}</p>
						</div>
					)}

					{/* Cost Summary */}
					{(wo.estimatedCost || wo.actualCost > 0) && (
						<div className="wo-detail-costs">
							<DollarOutlined className="wo-detail-section-icon" />
							<div className="wo-detail-cost-row">
								{wo.estimatedCost != null && (
									<div className="wo-detail-cost-item">
										<span className="wo-detail-cost-label">Estimated</span>
										<span className="wo-detail-cost-value">${Number(wo.estimatedCost).toLocaleString()}</span>
									</div>
								)}
								<div className="wo-detail-cost-item">
									<span className="wo-detail-cost-label">Actual</span>
									<span className="wo-detail-cost-value wo-detail-cost-actual">${Number(wo.actualCost).toLocaleString()}</span>
								</div>
							</div>
						</div>
					)}

					{/* Status Actions */}
					{nextActions.length > 0 && (
						<div className="wo-detail-actions">
							{nextActions.map((action) => (
								<Button
									key={action.status}
									icon={action.icon}
									loading={transitioning}
									onClick={() => handleStatusChange(action.status)}
									className="wo-detail-action-btn"
									style={{ borderColor: action.color, color: action.color }}
								>
									{action.label}
								</Button>
							))}
						</div>
					)}

					<Divider style={{ margin: "12px 0" }} />

					{/* FacilityLens Scans */}
					{flScans.length > 0 && (
						<>
							<div className="wo-detail-section">
								<h4><ScanOutlined /> FacilityLens</h4>
								{flScans.map((scan) => (
									<div key={scan.id} style={{ display: "flex", gap: 10, marginBottom: 10, padding: 10, border: "1px solid #e8e8ef", borderRadius: 8, background: "#fafbff" }}>
										<img
											src={scan.primaryPhotoUrl}
											alt="Scan"
											style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6 }}
										/>
										<div style={{ flex: 1 }}>
											<strong style={{ fontSize: 14 }}>
												{scan.aiIdentification?.assetType || "Asset Scan"}
											</strong>
											{scan.aiIdentification?.manufacturer && (
												<span style={{ color: "#0984e3", fontSize: 12, display: "block" }}>
													{scan.aiIdentification.manufacturer}
												</span>
											)}
											{scan.selectedIssues && scan.selectedIssues.length > 0 && (
												<div style={{ marginTop: 4 }}>
													{scan.selectedIssues.map((iss, i) => (
														<Tag key={i} color="volcano" style={{ fontSize: 10 }}>{iss.title}</Tag>
													))}
												</div>
											)}
										</div>
									</div>
								))}
							</div>
							<Divider style={{ margin: "12px 0" }} />
						</>
					)}

					{/* Activity Log */}
					<div className="wo-detail-activity">
						<h4>Activity</h4>
						{wo.activityLog && wo.activityLog.length > 0 ? (
							<Timeline
								items={wo.activityLog.map((entry) => ({
									color: entry.isSystemEvent ? "gray" : "blue",
									children: (
										<div className="wo-activity-item">
											<div className="wo-activity-header">
												<span className="wo-activity-user">
													{entry.userName ?? "System"}
												</span>
												<span className="wo-activity-time">
													{formatDate(entry.createdAt)}
												</span>
											</div>
											{entry.comment ? (
												<p className="wo-activity-comment">{entry.comment}</p>
											) : (
												<span className="wo-activity-action">
													{entry.action.replace(/_/g, " ").toLowerCase()}
													{entry.details && (entry.details as any).from && (
														<> — {(entry.details as any).from} → {(entry.details as any).to}</>
													)}
												</span>
											)}
										</div>
									),
								}))}
							/>
						) : (
							<p className="wo-detail-empty">No activity yet</p>
						)}
					</div>

					{/* Add Comment */}
					<div className="wo-detail-comment-input">
						<TextArea
							value={comment}
							onChange={(e) => setComment(e.target.value)}
							placeholder="Add a comment..."
							rows={2}
							maxLength={1000}
						/>
						<Button
							type="primary"
							icon={<SendOutlined />}
							loading={submittingComment}
							onClick={handleAddComment}
							disabled={!comment.trim()}
							className="wo-detail-comment-submit"
						>
							Send
						</Button>
					</div>
				</div>
			)}
		</Drawer>
	);
};
